import {HoistModel, MenuItemLike} from '@xh/hoist/core';
import {contextMenu} from '@xh/hoist/desktop/cmp/contextmenu';
import {CytoscapeInteractions} from './CytoGraphInteractions';
import {createRef} from 'react';
import {compact, first, isEmpty, merge} from 'lodash';
import {CytoGraphProps} from './CytoGraph';
import {CtxMenuArgs, EdgeCreatedArgs, INodeData, NodeMoveArgs, TargetType} from './CytoGraphTypes';
import {showContextMenu} from '@xh/hoist/kit/blueprint';
import {getDefaultLayout} from './CytoGraphDefaults';

export class CytoGraphModel extends HoistModel {
    private cytoscapeInteractions = new CytoscapeInteractions();
    cyObj: cytoscape.Core;
    graphPanelRef = createRef<HTMLElement>();
    selectedNodeId: string;

    constructor() {
        super();
    }

    override async doLoadAsync(c) {
        this.resetView();
    }

    override afterLinked(): void {
        this.cytoscapeInteractions.registerHandlers(this);

        const {nodeHtmlStyles} = this.getComponentProps();
        if (!isEmpty(nodeHtmlStyles)) (<any>this.cyObj).nodeHtmlLabel(nodeHtmlStyles, {enablePointerEvents: true});
    }

    onNodeClicked(id: string) {
        if (id !== this.selectedNodeId) {
            this.selectedNodeId = id;
            this.getComponentProps().onNodeClickFn?.(id);
        }
    }

    onNodeDblClicked(id: string) {
        if (id !== this.selectedNodeId) {
            this.selectedNodeId = id;
        }
        this.getComponentProps()?.onNodeDblClickFn?.(id);
    }

    onNodeMoved(args: NodeMoveArgs) {
        this.getComponentProps().onNodeMoveFn?.(args);
    }

    onCtxMenu(args: CtxMenuArgs) {
        const panelOffset = this.graphPanelRef?.current?.getBoundingClientRect();
        let menuItems: MenuItemLike[];
        switch (args.targetType) {
            case TargetType.TARGET_TYPE_NODE:
                menuItems = this.getComponentProps()?.nodeContextMenuItemsFn(args);
                break;
            case TargetType.TARGET_TYPE_GRAPH:
                menuItems = this.getComponentProps()?.graphContextMenuItemsFn(args);
                break;
            case TargetType.TARGET_TYPE_EDGE:
                menuItems = this.getComponentProps()?.edgeContextMenuItemsFn(args);
                break;
        }

        menuItems = compact(menuItems);
        if (isEmpty(menuItems)) return;

        showContextMenu(contextMenu({menuItems}), {
            left: args.menuOffset.x + panelOffset.left,
            top: args.menuOffset.y + panelOffset.top
        });
    }

    onEdgeCreated(args: EdgeCreatedArgs): boolean {
        return this.getComponentProps().onEdgeDrawnFn?.(args);
    }

    resetLayout() {
        const layout = this.cyObj.layout(this.getComponentProps().layout ?? getDefaultLayout());
        layout.run();
        this.cyObj.zoom(1);
        const node = this.cyObj.getElementById(first(this.rootIds));
        if (node) this.cyObj.center(node);
        this.getComponentProps().onLayoutChangedFn?.();
    }

    resetView() {
        const node = this.cyObj.getElementById(first(this.rootIds));
        if (node) {
            this.cyObj.elements().unselect();
            this.cyObj.center(node);
            this.cyObj.zoom(1);
        }
    }

    getNode(id: string) {
        return this.cyObj.getElementById(id);
    }

    get rootIds(): string[] {
        return this.cyObj
            .nodes()
            .roots()
            .map(n => n.data().id);
    }

    selectElement(id: string) {
        const element = this.cyObj.getElementById(id);
        element.select();
        this.getComponentProps().onNodeClickFn?.(id);
    }

    centerSelectNode(id: string) {
        this.expandAll();
        const node = this.cyObj.getElementById(id);
        if (node) {
            this.cyObj.elements().unselect();
            this.cyObj.center(node);
            this.selectElement(id);
        }
    }

    expandCollapse(nodeId) {
        const node = this.cyObj.getElementById(nodeId);
        if (!node) return;

        let display = 'none';
        try {
            const childEdge = first(node.connectedEdges().filter(e => e.data().source == nodeId));
            if (childEdge) {
                display = childEdge.targets()[0].style('display');
            }
        } catch (e) {
            console.warn(e);
        }

        if (display === 'none') {
            node.connectedEdges().targets().style('display', 'element');
            const data = node.data();
            data.customData.collapsed = false;
            node.data(data);

            node.data(merge(node.data()), {customData: {collapsed: false}});
            node.connectedEdges()
                .targets()
                .forEach(n => {
                    const data = n.data() as INodeData;
                    data.style.display = data.style.defaultDisplay ?? 'flex';
                    n.data(data);
                });
        } else {
            node.successors().targets().style('display', 'none');
            const data = node.data();
            data.customData.collapsed = true;
            node.data(data);

            node.successors()
                .targets()
                .forEach(n => {
                    const data = n.data() as INodeData;
                    data.style.display = 'none';
                    n.data(data);
                });
        }
    }

    expandAll() {
        this.cyObj.elements().style('display', 'element');
        this.cyObj.nodes().forEach(n => {
            const data = n.data();
            data.customData.collapsed = false;
            data.style.display = data.style.defaultDisplay ?? 'flex';
            n.data(data);
        });
    }

    private getComponentProps(): CytoGraphProps {
        return this.componentProps as CytoGraphProps;
    }
}
